import { hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { hasOptionalPropertyOfType } from "@ciaopeople/cp-shatsu/CustomTypes";
import { getEnumTypeChecker, inEnum, isArrayOf, isBoolean, isNumber, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { AuthProviderIdentifier } from "../../login/AuthProviderIdentifier.js";
import { NotificationDeviceType } from "./SubscriptionTypes.js";

/**
 * enumerativo che rappresenta il sesso dell'utente
 */
export enum UserGender  {
    UNKNOWN = 0,
    MALE = 1,
    FEMALE = 2
}


export enum UserGenderString {
    MALE = "male",
    FEMALE = "female"
}

/**
 * data object che contiene sesso ed età dell'utente
 */
export interface UserAgeAndSex {
    age?: number,
    sex?: UserGender,
    birthday?: string
}

export function isUserAgeAndSex(obj: any): obj is UserAgeAndSex {
    return isObject(obj) && 
        hasOptionalPropertyOfType(obj, "age", isNumber) &&
        hasOptionalPropertyOfType(obj, "sex", getEnumTypeChecker(UserGender)) &&
        hasOptionalPropertyOfType(obj, "birthday", isString);
}


/**
 * contiene tutti i dettagli sul nome e il genere dell'utente
 */
export interface UserNameAndGender {
    full_name: string,
    first_name?: string,
    last_name?: string,
    middle_name?: string,
    gender?: UserGenderString
}

export function isUserNameAndGender(obj: any): obj is UserNameAndGender {
    return isObject(obj) && 
        isString(obj.full_name) &&
        hasOptionalPropertyOfType(obj, "first_name", isString) && 
        hasOptionalPropertyOfType(obj, "last_name", isString) && 
        hasOptionalPropertyOfType(obj, "middle_name", isString) && 
        hasOptionalPropertyOfType(obj, "gender", getEnumTypeChecker(UserGenderString));
}


/**
 * interfaccia base di un Registration ID (sottoscrizione)
 */
interface BaseDeviceRI {
    deviceId: string,
    applicationId: string
}


export function isBaseDeviceRI(obj: any): obj is BaseDeviceRI {
    return isObject(obj) && 
        isString(obj.deviceId) &&
        isString(obj.applicationId);
}


/**
 * Full Registration ID per un account GCM android
 */
export interface AndroidFullRI extends BaseDeviceRI {
    ri: string
}


export function isAndroidFullRI(obj: any): obj is AndroidFullRI {
    return isBaseDeviceRI(obj) && 
        isString(obj['ri']);
}


/**
 * Full Registration ID per un endpoint webpush (gestito fuori da firebase)
 */
export interface WebPushFullRI extends BaseDeviceRI {
    endpoint: string
}

export function isWebPushFullRI(obj: any): obj is WebPushFullRI {
    return isBaseDeviceRI(obj) && 
        isString(obj['endpoint']);
}


/**
 * Firebase Device Token. contiene tutte le informazioni di una sottoscrizione
 * effettuata attraverso Firebase Cloud Messaging
 */
export interface FirebaseDeviceToken extends BaseDeviceRI {
    token: string,
    deviceName?: string,
    deviceType: NotificationDeviceType 
}


export function isFirebaseDeviceToken(obj: any): obj is FirebaseDeviceToken {
    return isBaseDeviceRI(obj) && 
        isString(obj['token']) &&
        hasOptionalPropertyOfType(obj, "deviceName", isString) &&
        inEnum(obj['deviceType'], NotificationDeviceType);
}


/**
 * contiene tutte le possibili sottoscrizionbi di un utente
 */
export interface UserSubscriptions {
    android: AndroidFullRI[],
    webPush: WebPushFullRI[],
    firebase: FirebaseDeviceToken[]
}

export function isUserSubscriptions(obj: any): obj is UserSubscriptions {
    return isObject(obj) && 
            isArrayOf(obj.android, isAndroidFullRI) &&
            isArrayOf(obj.webPush, isWebPushFullRI) &&
            isArrayOf(obj.firebase, isFirebaseDeviceToken);
}


/**
 * dati utente relativi ad uno dei provider di terze parti
 */
export interface UserThirdPartyAccountData {
    id: string,
    verified?: boolean,
    updateTime?: string
}

export interface UserLoginProviderInfo {
    providerId: AuthProviderIdentifier,
    userId: string
}

export function isUserLoginProviderInfo(obj: any): obj is UserLoginProviderInfo {
    return isObject(obj) &&
                hasOwnProperty(obj,"providerId") &&
                    hasOwnProperty(obj, "userId") &&
                        inEnum(obj["providerId"], AuthProviderIdentifier);
}


export function isUserThirdPartyAccountData(obj: any): obj is UserThirdPartyAccountData {
    return isObject(obj) && 
            isString(obj.id) &&
            hasOptionalPropertyOfType(obj, "verified", isBoolean) &&
            hasOptionalPropertyOfType(obj, "updateTime", isString);
}


/**
 * raccolta dei dati dei provider di terze parti utilizzati dall'utente
 */
type UserThirdPartyAccounts = {
    [k in AuthProviderIdentifier]?: UserThirdPartyAccountData
};


export function isUserThirdPartyAccounts(obj: any): obj is UserThirdPartyAccounts {
    return isObject(obj) && Object.values(AuthProviderIdentifier).reduce(
        (carry, cur) => {
            return carry && hasOptionalPropertyOfType(obj, cur, isUserThirdPartyAccountData);
        },
        true
    );
}


/**
 * raccoglie gli argomenti seguiti dall'utente
 */
export interface UserFollowsObj {
    authors: string[],
    locations: string[],
    categories: string[],
    stories: string[],
    channels: string[]
}

export function isUserFollowsObj(obj: any): obj is UserFollowsObj {
    return isObject(obj) && 
            isArrayOf(obj.authors, isString) &&
            isArrayOf(obj.locations, isString) &&
            isArrayOf(obj.categories, isString) &&
            isArrayOf(obj.stories, isString) &&
            isArrayOf(obj.channels, isString);
}

export interface Image {
    url: string;
    format?: string;
}

export function isImage(ele: any): ele is Image {
    return isObject(ele) && hasOwnProperty(ele, "url") && (!hasOwnProperty(ele, "format") || isString(ele.format));
}


export interface HasUserAccessToken {
    access_token: string
}


export function isHasUserAccessToken(ele: any): ele is HasUserAccessToken {
    return isObject(ele) && isString(ele.access_token);
}



export interface UserBasicInfo {

    id: string,
    user_info?: UserAgeAndSex,
    nameAndGender?: UserNameAndGender,
    email?: string,
    image?: Image,
    isMember?: boolean
}

export interface UserFullInfo extends UserBasicInfo {

    notifications: UserSubscriptions,
    social?: UserThirdPartyAccounts,
    follows: UserFollowsObj,
    loginProviderInfo?: UserLoginProviderInfo[],
    joinDateEpoch: number,
    hasMembershipBillingItems: boolean
    isUnloggedMember?: boolean,

    membership_type?: string,
    membership_status?: boolean,
    hasMembershipCard?: boolean
    hasBeenFreeTrial?: boolean
    isFreeTrial?: boolean
}


export function isUserBasicInfo(ele: any): ele is UserBasicInfo {
    return isObject(ele) &&
        isString(ele['id']) && 
        hasOptionalPropertyOfType(ele, "user_info", isUserAgeAndSex) &&
        hasOptionalPropertyOfType(ele, "nameAndGender", isUserNameAndGender) &&
        hasOptionalPropertyOfType(ele, "email", isString) &&
        hasOptionalPropertyOfType(ele, "image", isImage) && 
        hasOptionalPropertyOfType(ele, "isMember", isBoolean);
}


export function isUserFullInfo(ele: any): ele is UserFullInfo {
    return isUserBasicInfo(ele) &&
            isUserSubscriptions(ele['notifications'] ) && 
            hasOptionalPropertyOfType(ele, "social", isUserThirdPartyAccounts) &&
            // isString(ele['lastTouch']) && 
            isUserFollowsObj(ele['follows']) &&
            hasOptionalPropertyOfType(ele, "hasMembershipBillingItems", isBoolean) &&
            hasOptionalPropertyOfType(ele, "isUnloggedMember", isBoolean) &&
            hasOptionalPropertyOfType(ele, "membership_type", isString) &&
            hasOptionalPropertyOfType(ele, "membership_status", isBoolean) &&
            hasOptionalPropertyOfType(ele, "hasMembershipCard", isBoolean) &&
            hasOptionalPropertyOfType(ele, "hasBeenFreeTrial", isBoolean) &&
            hasOptionalPropertyOfType(ele, "isFreeTrial", isBoolean) &&
            hasOptionalPropertyOfType(ele, "loginProviderInfo", isUserLoginProviderInfo);

}


export interface OnLoginResponse extends HasUserAccessToken {
    result: string,
    user_info?: UserAgeAndSex,
    user_name?: UserNameAndGender,
    isNewUser: boolean
}

export function isOnLoginResponse(ele: any): ele is OnLoginResponse {
    return isObject(ele) && isHasUserAccessToken(ele) &&
        isString(ele['result']) &&
        hasOptionalPropertyOfType(ele, "user_info", isUserAgeAndSex) &&
        hasOptionalPropertyOfType(ele, "user_name", isUserNameAndGender) &&
        isBoolean(ele['isNewUser']);
}

export interface GrantStatusResponseContent {
    valid: boolean;
}

export function isGrantStatusResponseContent(ele: any): ele is GrantStatusResponseContent {
    return isObject(ele) && isBoolean(ele.valid);
}


export interface OnUserUpdateData {
    email?: string,
    userBirthday?: string|number,
    userGender?: UserGender,
    birthdayFromAge?: boolean,
    alternativeNaming?: [string,string]
}


export enum EmailLoginSourceContext {
   ANDROID_APP = "android",
   IOS_APP = "ios",
   OTHER = "other" 
}


export interface Ask1stPartyEmailAuthData {

    email: string,
    native_app?: EmailLoginSourceContext,
    redirect_uri?: string,
    session_funnel_id?: string,
    auth_context?: string,
    first_name?: string
    last_name?: string,
    gender?: UserGender,
    birthday?: string
}


export function isAsk1stPartyEmailAuthData(x: any): x is Ask1stPartyEmailAuthData {
    return isObject(x) &&
        ("email" in x && isString(x.email)) &&
        (!("native_app" in x) || inEnum(x.native_app, EmailLoginSourceContext)) &&
        (!("redirect_uri" in x) || isString(x.redirect_uri)) &&
        (!("session_funnel_id" in x) || isString(x.session_funnel_id)) &&
        (!("auth_context" in x) || isString(x.auth_context)) &&
        (!("first_name" in x) || isString(x.first_name)) &&
        (!("last_name" in x) || isString(x.last_name)) &&
        (!("gender" in x) || inEnum(x.gender, UserGender)) &&
        (!("birthday" in x) || isString(x.birthday));

}